import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useClient } from 'urql';
import CustomModal from '../../../components/configuracion/components/customModal';
import { formatRut, rutWithDash } from '../../../utils/formatter';
import { Icon } from 'semantic-ui-react';
import { debounce } from '../../../utils/debounce';
import { FindCompanyName } from '../../../graphql/addCompany';
import { CreateCompany, GetCompanyGroups, NEXT_COMPANY_CORRELATIVE, IS_COMPANY_CORRELATIVE_AVAILABLE } from '../../../graphql/company';
import CompanyGroupInput from '../../../components/shared/CompanyGroupInput/CompanyGroupInput';
import './CreateCompanyModal.scss';

const PLAN_OPTIONS = [
  { value: 'servicio1', labelKey: 'plans.servicio1' },
  { value: 'servicio2', labelKey: 'plans.servicio2' },
  { value: 'servicio3', labelKey: 'plans.servicio3' },
  { value: 'anualizado', labelKey: 'plans.anualizado' },
];

const ONBOARDING_TYPE_OPTIONS = [
  { value: 'classic', labelKey: 'onboardingTypes.classic', hintKey: 'hints.onboardingClassic' },
  { value: 'direct', labelKey: 'onboardingTypes.direct', hintKey: 'hints.onboardingDirect' },
  { value: 'sii', labelKey: 'onboardingTypes.sii', hintKey: 'hints.onboardingSii' },
];

const ADDITIONAL_SERVICE_OPTIONS = [
  { value: 'CFO Externo', labelKey: 'additionalServices.cfoExterno' },
  { value: 'RRHH', labelKey: 'additionalServices.rrhh' },
  { value: 'Operaciones Financieras - Facturación', labelKey: 'additionalServices.operacionesFacturacion' },
  { value: 'Contabilidad - Contabilidad Mensual bajo IFRS', labelKey: 'additionalServices.contabilidadMensual' },
  { value: 'Contabilidad - Reconstrucción Contable', labelKey: 'additionalServices.contabilidadReconstruccion' },
  { value: 'Finanzas - Consolidación de Empresas', labelKey: 'additionalServices.finanzasConsolidacion' },
  { value: 'Finanzas - Apoyo de Licitaciones', labelKey: 'additionalServices.finanzasLicitaciones' },
  { value: 'Finanzas - Informes Institucionales', labelKey: 'additionalServices.finanzasInformes' },
];

const DEFAULT_ADDITIONAL_SERVICE = ADDITIONAL_SERVICE_OPTIONS[0].value;

// Asterisco rojo para marcar campos obligatorios
const RequiredMark = () => (
  <span className="required-mark" aria-hidden="true" style={{ color: '#c62828', marginLeft: 4 }}>*</span>
);

// Validación de RUT chileno mediante módulo 11
const isValidRut = (rut) => {
  if (!rut) return false;
  const clean = String(rut).replace(/[^0-9kK]/g, '').toUpperCase();
  if (clean.length < 7 || clean.length > 9) return false;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  let sum = 0;
  let mul = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i], 10) * mul;
    mul = mul === 7 ? 2 : mul + 1;
  }
  const mod = 11 - (sum % 11);
  const expected = mod === 11 ? '0' : mod === 10 ? 'K' : String(mod);
  return expected === dv;
};

// Validación básica de formato de email
const isValidEmail = (email) => {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
};

// Estilo común para mensajes de error inline
const fieldErrorStyle = {
  color: '#c62828',
  fontSize: '12px',
  marginTop: '4px'
};

/** Primer día del mes en curso; si hoy es día 20+, primer día del mes siguiente. */
const getDefaultCompanyEntryDate = () => {
  const today = new Date();
  let year = today.getFullYear();
  let month = today.getMonth();

  if (today.getDate() >= 20) {
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }

  return `${year}-${String(month + 1).padStart(2, '0')}-01`;
};

const createInitialFormData = () => ({
  rut: '',
  name: '',
  alias: '',
  comments: '',
  group: '',
  providerPaymentDays: 30,
  clientPaymentDays: 30,
  scfo_plan: 'servicio1',
  entryDate: getDefaultCompanyEntryDate(),
  onboardingEmails: [''],
  selectedServices: [],
  onboardingType: 'classic',
  correlativeKind: 'MP',
});

const CreateCompanyModal = ({ open, onClose }) => {
  const { t } = useTranslation('header');
  const tm = (key, opts) => t(`company.createModal.${key}`, opts);

  const [formData, setFormData] = useState(createInitialFormData);

  // Obtener grupos desde la base de datos
  const { data: groupsData, reexecuteQuery: refetchGroups } = GetCompanyGroups();
  const savedGroups = groupsData?.getCompanyGroups || [];

  const [showSuccess, setShowSuccess] = useState(false);
  const [onboardingUrl, setOnboardingUrl] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { executeMutation: findCompanyName } = FindCompanyName();
  const { executeMutation: createCompany, fetching: isCreating } = CreateCompany();
  const urqlClient = useClient();
  const correlativeKind = formData.correlativeKind || 'MP';
  const hasCorrelative = correlativeKind !== 'NONE';
  const [proposedCorrelative, setProposedCorrelative] = useState('');
  const [correlativeTouched, setCorrelativeTouched] = useState(false);
  const correlativeTouchedRef = useRef(false);
  const [fetchingNextCorrelative, setFetchingNextCorrelative] = useState(false);
  const [nextCorrelativeError, setNextCorrelativeError] = useState(null);
  const [correlativeTaken, setCorrelativeTaken] = useState(false);
  const [checkingCorrelative, setCheckingCorrelative] = useState(false);
  const [isFindingCompany, setIsFindingCompany] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFormData(createInitialFormData());
    setShowSuccess(false);
    setOnboardingUrl('');
    setIsCopied(false);
    setErrorMessage('');
    setProposedCorrelative('');
    setCorrelativeTouched(false);
    correlativeTouchedRef.current = false;
    setNextCorrelativeError(null);
    setCorrelativeTaken(false);
    setCheckingCorrelative(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    if (!hasCorrelative) {
      setFetchingNextCorrelative(false);
      setNextCorrelativeError(null);
      return undefined;
    }
    let cancelled = false;
    setFetchingNextCorrelative(true);
    setNextCorrelativeError(null);
    setCorrelativeTaken(false);
    urqlClient
      .query(
        NEXT_COMPANY_CORRELATIVE,
        { kind: correlativeKind },
        { requestPolicy: 'network-only' },
      )
      .toPromise()
      .then((result) => {
        if (cancelled) return;
        if (result.error) {
          if (!correlativeTouchedRef.current) setProposedCorrelative('');
          setNextCorrelativeError(result.error);
          return;
        }
        const next = result.data?.nextCompanyCorrelative || '';
        if (!correlativeTouchedRef.current) setProposedCorrelative(next);
      })
      .catch((err) => {
        if (cancelled) return;
        if (!correlativeTouchedRef.current) setProposedCorrelative('');
        setNextCorrelativeError(err);
      })
      .finally(() => {
        if (!cancelled) setFetchingNextCorrelative(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, correlativeKind, hasCorrelative, urqlClient]);

  const isCorrelativeFormatValid = (value, kind) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return false;
    return kind === 'MP' ? /^MP\d+$/i.test(trimmed) : /^\d+(\.\d+)?$/.test(trimmed);
  };

  const checkCorrelativeAvailability = useRef(
    debounce(async (value, kind) => {
      const trimmed = String(value || '').trim();
      if (!trimmed || !isCorrelativeFormatValid(trimmed, kind)) {
        setCorrelativeTaken(false);
        setCheckingCorrelative(false);
        return;
      }
      setCheckingCorrelative(true);
      try {
        const result = await urqlClient
          .query(
            IS_COMPANY_CORRELATIVE_AVAILABLE,
            { correlative: trimmed },
            { requestPolicy: 'network-only' },
          )
          .toPromise();
        if (result.error) {
          setCorrelativeTaken(false);
          return;
        }
        setCorrelativeTaken(result.data?.isCompanyCorrelativeAvailable === false);
      } catch (_) {
        setCorrelativeTaken(false);
      } finally {
        setCheckingCorrelative(false);
      }
    }, 400),
  ).current;

  useEffect(() => {
    if (!open) return;
    if (fetchingNextCorrelative) return;
    const value = proposedCorrelative.trim();
    if (!value || !isCorrelativeFormatValid(value, correlativeKind)) {
      setCorrelativeTaken(false);
      setCheckingCorrelative(false);
      return;
    }
    checkCorrelativeAvailability(value, correlativeKind);
  }, [open, proposedCorrelative, correlativeKind, fetchingNextCorrelative, checkCorrelativeAvailability]);

  const handleCorrelativeKindChange = (kind) => {
    correlativeTouchedRef.current = false;
    setCorrelativeTouched(false);
    setProposedCorrelative('');
    setCorrelativeTaken(false);
    handleInputChange('correlativeKind', kind);
  };

  const handleCorrelativeChange = (e) => {
    correlativeTouchedRef.current = true;
    setCorrelativeTouched(true);
    setCorrelativeTaken(false);
    setProposedCorrelative(e.target.value);
  };

  const onboardingHint = useMemo(() => {
    const opt = ONBOARDING_TYPE_OPTIONS.find((o) => o.value === formData.onboardingType);
    return opt ? tm(opt.hintKey) : '';
  }, [formData.onboardingType, t]);

  // Debounced RUT lookup to auto-fill Razón Social
  const debouncedFindCompanyName = useRef(
    debounce(async (rut) => {
      try {
        if (!rut) {
          setIsFindingCompany(false);
          return;
        }
        setIsFindingCompany(true);
        const { data, error } = await findCompanyName({ rut });
        if (error) return;
        if (data && data !== 'no') {
          const value = data.findCompanyName;
          if (value) {
            setFormData(prev => ({ ...prev, name: value }));
          }
        }
      } catch (_) {
        // silent
      } finally {
        setIsFindingCompany(false);
      }
    }, 800)
  ).current;


  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRutChange = (e) => {
    const newValue = e.target.value.replace(/[^0-9Kk]/g, '');
    handleInputChange('rut', newValue);
    // Trigger debounced lookup using normalized RUT
    debouncedFindCompanyName(rutWithDash(newValue));
  };

  const handleNameChange = (e) => {
    handleInputChange('name', e.target.value);
  };

  const handleAliasChange = (e) => {
    handleInputChange('alias', e.target.value);
  };

  const handleCommentsChange = (e) => {
    handleInputChange('comments', e.target.value);
  };

  const handleProviderPaymentDaysChange = (e) => {
    handleInputChange('providerPaymentDays', parseInt(e.target.value));
  };

  const handleClientPaymentDaysChange = (e) => {
    handleInputChange('clientPaymentDays', parseInt(e.target.value));
  };

  const handlePlanChange = (e) => {
    handleInputChange('scfo_plan', e.target.value);
  };

  const handleEntryDateChange = (e) => {
    handleInputChange('entryDate', e.target.value);
  };

  const handleOnboardingTypeChange = (e) => {
    handleInputChange('onboardingType', e.target.value);
  };

  const addSelectedService = (service) => {
    const newService = {
      service: service,
      startDate: '',
      id: Date.now() + Math.random()
    };
    const newServices = [...formData.selectedServices, newService];
    handleInputChange('selectedServices', newServices);
  };

  const removeSelectedService = (serviceId) => {
    const newServices = formData.selectedServices.filter(s => s.id !== serviceId);
    handleInputChange('selectedServices', newServices);
  };

  const updateServiceValue = (serviceId, field, value) => {
    const newServices = formData.selectedServices.map(s => 
      s.id === serviceId ? { ...s, [field]: value } : s
    );
    handleInputChange('selectedServices', newServices);
  };

  const handleOnboardingEmailChange = (index, value) => {
    const newEmails = [...formData.onboardingEmails];
    newEmails[index] = value;
    handleInputChange('onboardingEmails', newEmails);
  };

  const addOnboardingEmail = () => {
    const newEmails = [...formData.onboardingEmails, ''];
    handleInputChange('onboardingEmails', newEmails);
  };

  const removeOnboardingEmail = (index) => {
    if (formData.onboardingEmails.length > 1) {
      const newEmails = formData.onboardingEmails.filter((_, i) => i !== index);
      handleInputChange('onboardingEmails', newEmails);
    }
  };

  const handleConfirm = async () => {
    try {
      // Validación defensiva por si el botón llegara a habilitarse indebidamente
      if (!isFormValid) {
        setErrorMessage(tm('errors.requiredFields'));
        return;
      }
      // Prepare the input data for the GraphQL mutation
      const input = {
        rut: rutWithDash(formData.rut),
        name: formData.name,
        alias: formData.alias,
        comments: formData.comments,
        group: formData.group ? {
          groupName: formData.group.trim(),
          assignedAt: new Date(),
          assignedBy: null
        } : null,
        providerPaymentDays: formData.providerPaymentDays,
        clientPaymentDays: formData.clientPaymentDays,
        scfo_plan: formData.scfo_plan, // SCFO plan selection
        entryDate: formData.entryDate ? new Date(formData.entryDate) : null,
        onboardingEmails: formData.onboardingEmails.filter(email => email.trim()),
        selectedServices: formData.selectedServices,
        onboardingType: formData.onboardingType, // Include onboarding type
        correlativeKind,
        correlative: hasCorrelative ? proposedCorrelative.trim() || undefined : undefined,
      };

      const { data, error } = await createCompany({ input });
      
      if (error) {
        const gqlMessage =
          error.graphQLErrors?.[0]?.message ||
          error.message ||
          '';
        if (/correlativo/i.test(gqlMessage)) {
          setCorrelativeTaken(true);
          setErrorMessage(gqlMessage);
        } else {
          setErrorMessage(gqlMessage || tm('errors.connection'));
        }
        return;
      }

      const response = data?.createCompany;
      
      if (response?.status) {
        // Refrescar la lista de grupos desde la base de datos
        refetchGroups({ requestPolicy: 'network-only' });
        // Show success state with onboarding URL
        setOnboardingUrl(response.data?.onboarding?.url || '');
        setShowSuccess(true);
        setErrorMessage('');
      } else {
        // Show error message from backend
        const message = response?.message || tm('errors.unknown');
        if (/correlativo/i.test(message)) {
          setCorrelativeTaken(true);
        }
        setErrorMessage(message);
      }
    } catch (err) {
      setErrorMessage(tm('errors.unexpected'));
    }
  };

  const handleCancel = () => {
    setFormData(createInitialFormData());
    setShowSuccess(false);
    setOnboardingUrl('');
    setIsCopied(false);
    setErrorMessage('');
    setProposedCorrelative('');
    setCorrelativeTouched(false);
    correlativeTouchedRef.current = false;
    setCorrelativeTaken(false);
    setCheckingCorrelative(false);
    onClose();
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(onboardingUrl).then(() => {
      setIsCopied(true);
      
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    }).catch(err => {
      // Silent fail for copy operation
    });
  };

  // Cálculo de validez por campo (se usan también para mostrar errores inline)
  const rutHasValue = !!formData.rut.trim();
  const rutValid = isValidRut(formData.rut);
  const nameValid = !!formData.name.trim();
  const aliasValid = !!formData.alias.trim();
  const planValid = !!formData.scfo_plan;
  const onboardingTypeValid = !!formData.onboardingType;
  const entryDateValid = !!formData.entryDate;
  const filledEmails = formData.onboardingEmails.filter(e => e && e.trim());
  const emailsAllValid = filledEmails.length > 0 && filledEmails.every(isValidEmail);
  const correlativeValue = proposedCorrelative.trim();
  const correlativeValid =
    correlativeKind === 'MP'
      ? /^MP\d+$/i.test(correlativeValue)
      : /^\d+(\.\d+)?$/.test(correlativeValue);
  const correlativeAvailable =
    !hasCorrelative || (correlativeValid && !correlativeTaken && !checkingCorrelative);

  const isFormValid =
    rutValid &&
    nameValid &&
    aliasValid &&
    planValid &&
    onboardingTypeValid &&
    entryDateValid &&
    emailsAllValid &&
    correlativeAvailable &&
    !fetchingNextCorrelative;

  return (
    <CustomModal
      open={open}
      onClose={handleCancel}
      title={tm('title')}
      content={
        showSuccess ? (
          <div className="create-company-modal-content success-content">
            <div className="success-message">
              <Icon name="checkmark circle" color="green" size="huge" />
              <h2>{tm('success.title')}</h2>
              <p>{tm('success.description')}</p>
            </div>
            
            <div className="onboarding-url-section">
              <label className="label" htmlFor="onboarding-url">{tm('success.onboardingLink')}</label>
              <div className="url-input-container">
                <input 
                  id="onboarding-url"
                  type="text" 
                  value={onboardingUrl}
                  readOnly
                  className="onboarding-url-input"
                />
                <button 
                  type="button" 
                  className={`copy-button ${isCopied ? 'copied' : ''}`}
                  onClick={copyToClipboard}
                  disabled={isCopied}
                >
                  <Icon name={isCopied ? "check" : "copy"} />
                  {isCopied ? tm('success.copied') : tm('success.copy')}
                </button>
              </div>
            </div>
          </div>
        ) : (
        <div className="create-company-modal-content">
          {errorMessage && (
            <div className="error-message" style={{ 
              backgroundColor: '#ffebee', 
              color: '#c62828', 
              padding: '12px', 
              borderRadius: '4px', 
              marginBottom: '20px',
              border: '1px solid #ffcdd2'
            }}>
              <Icon name="exclamation triangle" color="red" />
              {errorMessage}
            </div>
          )}
          <div className="modal-sections">
            {/* Left Section */}
            <div className="left-section">
              {/* Empresa Section */}
              <div className="section-title">{tm('sections.company')}</div>
              <div className="field">
                <label className="label" htmlFor="company-rut">{tm('fields.rut')}<RequiredMark /></label>
                <input 
                  id="company-rut"
                  type="text" 
                  value={formatRut(formData.rut)}
                  onChange={handleRutChange}
                  placeholder={tm('placeholders.rut')}
                  maxLength="12"
                  required
                  aria-invalid={rutHasValue && !rutValid}
                />
                {rutHasValue && !rutValid ? (
                  <div style={fieldErrorStyle}>{tm('errors.invalidRut')}</div>
                ) : (
                  <div className="hint">{tm('hints.rutFormat')}</div>
                )}
              </div>

              <div className="field">
                <label className="label" htmlFor="company-name">{tm('fields.legalName')}<RequiredMark /></label>
                <div className={`input-wrapper${isFindingCompany ? ' loading' : ''}`}>
                  <input 
                    id="company-name"
                    type="text" 
                    value={formData.name}
                    onChange={handleNameChange}
                    placeholder={tm('placeholders.legalName')}
                    required
                    aria-invalid={!nameValid}
                  />
                </div>
              </div>

              <div className="field">
                <label className="label" htmlFor="company-alias">{tm('fields.alias')}<RequiredMark /></label>
                <input 
                  id="company-alias"
                  type="text" 
                  value={formData.alias}
                  onChange={handleAliasChange}
                  placeholder={tm('placeholders.alias')}
                  required
                  aria-invalid={!aliasValid}
                />
              </div>

              <div className="field">
                <label className="label">{tm('fields.correlative')}</label>
                <div
                  className={`correlative-input-group ${
                    hasCorrelative && ((correlativeTouched && !correlativeValid) || correlativeTaken)
                      ? 'invalid'
                      : ''
                  }`}
                >
                  <select
                    className="correlative-kind-select"
                    value={correlativeKind}
                    onChange={(e) => handleCorrelativeKindChange(e.target.value)}
                    aria-label={tm('fields.correlative')}
                  >
                    <option value="MP">{tm('fields.correlativeMp')}</option>
                    <option value="DEFINITIVE">{tm('fields.correlativeDefinitive')}</option>
                    <option value="NONE">{tm('fields.correlativeNone')}</option>
                  </select>
                  <input
                    id="company-correlative"
                    type="text"
                    className="correlative-proposed-input"
                    value={!hasCorrelative || (fetchingNextCorrelative && !correlativeTouched) ? '' : proposedCorrelative}
                    onChange={handleCorrelativeChange}
                    placeholder={
                      !hasCorrelative
                        ? ''
                        : fetchingNextCorrelative
                          ? '…'
                          : correlativeKind === 'MP'
                            ? 'MP1'
                            : '343'
                    }
                    disabled={!hasCorrelative}
                    aria-label={tm('fields.correlativeProposed')}
                    aria-invalid={hasCorrelative && ((correlativeTouched && !correlativeValid) || correlativeTaken)}
                    autoComplete="off"
                  />
                </div>
                {!hasCorrelative ? (
                  <div className="hint">{tm('hints.correlativeNone')}</div>
                ) : nextCorrelativeError ? (
                  <div className="hint" style={{ color: '#c62828' }}>
                    {nextCorrelativeError.graphQLErrors?.[0]?.message
                      || nextCorrelativeError.message
                      || 'No se pudo obtener el correlativo'}
                  </div>
                ) : correlativeTaken ? (
                  <div className="hint" style={{ color: '#c62828' }}>
                    {tm('errors.correlativeTaken', { correlative: correlativeValue })}
                  </div>
                ) : correlativeTouched && correlativeValue && !correlativeValid ? (
                  <div className="hint" style={{ color: '#c62828' }}>
                    {correlativeKind === 'MP'
                      ? tm('hints.correlativeMpFormat')
                      : tm('hints.correlativeDefinitiveFormat')}
                  </div>
                ) : checkingCorrelative ? (
                  <div className="hint">{tm('hints.correlativeChecking')}</div>
                ) : (
                  <div className="hint">{tm('hints.correlativeEditable')}</div>
                )}
              </div>

              <div className="field group-field">
                <label className="label" htmlFor="company-group">{tm('fields.groupOptional')}</label>
                <CompanyGroupInput
                  id="company-group"
                  value={formData.group}
                  groups={savedGroups}
                  onChange={(value) => handleInputChange('group', value)}
                  placeholder={tm('placeholders.group')}
                />
                <div className="hint">{tm('hints.group')}</div>
              </div>

              <div className="field">
                <label className="label" htmlFor="company-comments">{tm('fields.comments')}</label>
                <textarea
                  id="company-comments"
                  value={formData.comments}
                  onChange={handleCommentsChange}
                  placeholder={tm('placeholders.comments')}
                  rows={4}
                />
              </div>
            </div>

            {/* Vertical Divider */}
            <div className="vertical-divider"></div>

            {/* Right Section */}
            <div className="right-section">
              {/* Servicios Section */}
              <div className="section-title">{tm('sections.services')}</div>
              <div className="grid g-2">
                <div className="field">
                  <label className="label" htmlFor="company-plan">{tm('fields.contractedPlan')}<RequiredMark /></label>
                  <select 
                    id="company-plan" 
                    value={formData.scfo_plan}
                    onChange={handlePlanChange}
                    required
                  >
                    {PLAN_OPTIONS.map(({ value, labelKey }) => (
                      <option key={value} value={value}>{tm(labelKey)}</option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label className="label" htmlFor="company-entry-date">{tm('fields.entryDate')}<RequiredMark /></label>
                  <input 
                    id="company-entry-date"
                    type="date" 
                    value={formData.entryDate}
                    onChange={handleEntryDateChange}
                    required
                    aria-invalid={!entryDateValid}
                  />
                  {!entryDateValid && (
                    <div style={fieldErrorStyle}>{tm('errors.entryDateRequired')}</div>
                  )}
                </div>
              </div>

              {/* Onboarding Type Section */}
              <div className="field">
                <label className="label" htmlFor="onboarding-type">{tm('fields.onboardingType')}<RequiredMark /></label>
                <select 
                  id="onboarding-type" 
                  value={formData.onboardingType}
                  onChange={handleOnboardingTypeChange}
                  required
                >
                  {ONBOARDING_TYPE_OPTIONS.map(({ value, labelKey }) => (
                    <option key={value} value={value}>{tm(labelKey)}</option>
                  ))}
                </select>
                <div className="hint" style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {onboardingHint}
                </div>
              </div>

              {/* Additional Services */}
              <div className="field">
                <label className="label">{tm('fields.additionalServices')}</label>
                <div className="additional-services-container">
                  {formData.selectedServices.length > 0 && (
                    <div className="selected-services">
                      {formData.selectedServices.map((serviceItem) => (
                        <div key={serviceItem.id} className="service-row">
                          <div className="service-inputs">
                            <div className="field">
                              <label className="label">{tm('fields.additional')}</label>
                              <select 
                                value={serviceItem.service}
                                onChange={(e) => updateServiceValue(serviceItem.id, 'service', e.target.value)}
                                className="service-select"
                              >
                                {ADDITIONAL_SERVICE_OPTIONS.map(({ value, labelKey }) => (
                                  <option key={value} value={value}>{tm(labelKey)}</option>
                                ))}
                              </select>
                            </div>
                            <div className="field">
                              <label className="label">{tm('fields.serviceStartDate')}</label>
                              <input 
                                type="date" 
                                value={serviceItem.startDate}
                                onChange={(e) => updateServiceValue(serviceItem.id, 'startDate', e.target.value)}
                                className="service-date"
                              />
                            </div>
                          </div>
                          <button 
                            type="button" 
                            className="remove-service-btn"
                            onClick={() => removeSelectedService(serviceItem.id)}
                          >
                            <Icon name="minus" color="red" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button 
                    type="button" 
                    className="add-service-btn"
                    onClick={() => addSelectedService(DEFAULT_ADDITIONAL_SERVICE)}
                  >
                    <Icon name="plus" />
                    {tm('addService')}
                  </button>
                </div>
              </div>

              {/* Contacto Onboarding Section */}
              <div className="section-title">{tm('sections.onboardingEmail')}<RequiredMark /></div>
              {formData.onboardingEmails.map((email, index) => (
                <div key={index} className="email-field field">
                  <div className="email-input-container">
                    <input 
                      type="email" 
                      value={email}
                      onChange={(e) => handleOnboardingEmailChange(index, e.target.value)}
                      placeholder={tm('placeholders.contactEmail')}
                      required={index === 0}
                      aria-invalid={!!email && !isValidEmail(email)}
                    />
                    {index > 0 && (
                      <button 
                        type="button" 
                        className="remove-email-btn"
                        onClick={() => removeOnboardingEmail(index)}
                      >
                        <Icon name="minus" color="red" />
                      </button>
                    )}
                  </div>
                  {!!email && !isValidEmail(email) && (
                    <div style={fieldErrorStyle}>{tm('errors.invalidEmail')}</div>
                  )}
                  {index === 0 && !email && (
                    <div style={fieldErrorStyle}>{tm('errors.emailRequired')}</div>
                  )}
                </div>
              ))}
              <button 
                type="button" 
                className="add-email-btn"
                onClick={addOnboardingEmail}
                style={{ display: 'none' }}
              >
                <Icon name="plus" />
                {tm('addContact')}
              </button>
            </div>
          </div>
        </div>
        )
      }
      confirmLabel={showSuccess ? tm('close') : tm('create')}
      confirmDisabled={showSuccess ? false : (!isFormValid || isCreating)}
      isLoading={!showSuccess && isCreating}
      cancelarLabel={showSuccess ? undefined : tm('cancel')}
      onConfirm={showSuccess ? handleCancel : handleConfirm}
      size="tiny"
      classNameTitle="!tw-text-center !tw-text-[16px] !tw-font-semibold"
      classNameContent="create-company-modal-body"
      classNameModal="create-company-modal"
      dimmer={{ style: { zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.5)' } }}
      modalStyle={{
        width: '1300px',
        maxWidth: '95%',
        minWidth: 'min(1300px, 95%)',
        maxHeight: '80vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    />
  );
};

export default CreateCompanyModal;
